import { motion } from "framer-motion";
import { MessageCircle, Home, Phone } from "lucide-react";
import { useLanguage, waLink, PHONE_DISPLAY, PHONE_TEL } from "../i18n";
import LangToggle from "./LangToggle";
import PaymentBadges from "./PaymentBadges";
import { EmergencyBlock } from "./Availability";

const EASE = [0.22, 1, 0.36, 1];

const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer
      id="contacto"
      className="relative overflow-hidden bg-[#0D1B2A] pt-24 pb-10 text-[#FAF6F0]"
      data-testid="footer-section"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-32 bottom-0 h-96 w-96 rounded-full bg-[#25D366]/10 blur-3xl"
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center text-center">
          <motion.p
            initial={{ opacity: 0, rotate: -6 }}
            whileInView={{ opacity: 1, rotate: -2 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="font-script text-2xl text-[#F2A33C] sm:text-3xl"
          >
            {t.footer.script}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.75, delay: 0.08, ease: EASE }}
            className="mt-4 max-w-3xl font-serif text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl"
          >
            {t.footer.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.18, ease: EASE }}
            className="mt-5 max-w-xl text-base leading-relaxed text-[#FAF6F0]/60"
          >
            {t.footer.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.28, ease: EASE }}
            className="mt-9 flex flex-wrap items-center justify-center gap-4"
          >
            <a
              data-testid="footer-whatsapp-cta"
              href={waLink(t.waGeneric)}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2.5 rounded-full bg-[#25D366] px-8 py-4 text-base font-bold text-white shadow-xl shadow-[#25D366]/25 transition-all duration-300 hover:scale-[1.05] hover:shadow-2xl hover:shadow-[#25D366]/40"
            >
              <MessageCircle size={20} strokeWidth={2.4} className="transition-transform duration-300 group-hover:-rotate-12" />
              {t.footer.cta}
            </a>
            <a
              data-testid="footer-call-link"
              href={PHONE_TEL}
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#FAF6F0]/70 transition-colors duration-300 hover:text-white"
            >
              <Phone size={15} />
              {PHONE_DISPLAY}
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.38, ease: EASE }}
            className="mt-7 flex justify-center"
          >
            <PaymentBadges dark testid="footer-payment-icons" />
          </motion.div>
        </div>

        <EmergencyBlock />

        <div className="mt-20 flex flex-col items-center justify-between gap-8 border-t border-white/10 pt-8 md:flex-row">
          <div className="flex flex-col items-center gap-2.5 md:items-start">
            <span className="inline-flex rounded-2xl bg-[#FAF6F0] px-4 py-2.5 shadow-lg">
              <img src="/images/logo.png" alt="CasaFix Marina" className="h-14 w-auto" />
            </span>
            <span className="font-script text-sm text-[#F2A33C]">{t.footer.fix}</span>
          </div>

          <div className="flex flex-col items-center gap-2 text-sm text-[#FAF6F0]/50 md:items-end">
            <div data-testid="footer-lang-toggle">
              <LangToggle dark />
            </div>
            <p className="mt-2">{t.footer.area}</p>
            <p>{t.footer.rights}</p>
            <p className="font-script text-lg text-[#FAF6F0]/70">{t.footer.hands}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
